import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify } from "ra-core";
import {
  Copy,
  ExternalLink,
  FlaskConical,
  Plus,
  QrCode,
  RefreshCw,
} from "lucide-react";
import QRCode from "qrcode";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  listTeponeCodes,
  mintTeponeCodes,
  type TeponeAdminCode,
} from "./teponeClient";

const STATUS_META: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    className?: string;
  }
> = {
  minted: { label: "Not scanned", variant: "secondary" },
  opened: { label: "Scanned", variant: "outline" },
  locked: { label: "Uploading", variant: "outline" },
  ready: { label: "Awaiting WhatsApp", variant: "default" },
  claimed: {
    label: "Used — generating",
    variant: "default",
    className: "bg-emerald-600 hover:bg-emerald-600",
  },
  delivered: {
    label: "Used — delivered",
    variant: "default",
    className: "bg-emerald-700 hover:bg-emerald-700",
  },
  expired: { label: "Expired", variant: "destructive" },
  void: { label: "Void", variant: "destructive" },
};

// "Used" matches the code lifecycle: a code burns for other scanners at
// `ready` (Create pressed); claimed/delivered are used codes with a guest.
const USED_STATUSES = new Set(["ready", "claimed", "delivered"]);

function formatDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

async function downloadQr(code: TeponeAdminCode) {
  const dataUrl = await QRCode.toDataURL(code.portal_url, {
    width: 1024,
    margin: 2,
    errorCorrectionLevel: "M",
  });
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `${code.code}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function TePoneCodesPage() {
  const notify = useNotify();
  const [codes, setCodes] = useState<TeponeAdminCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCodes(await listTeponeCodes());
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleMint = useCallback(
    async (dev = false) => {
      setMinting(true);
      try {
        const fresh = await mintTeponeCodes(undefined, { dev });
        setCodes((current) => [...fresh, ...current]);
        const first = fresh[0];
        if (first) {
          notify(
            dev
              ? `Minted dev link ${first.code} — never expires, reusable`
              : `Minted ${first.code} — downloading its QR`,
            { type: "success" },
          );
          // Dev links are for pasting around internally — skip the QR download.
          if (!dev) await downloadQr(first);
        }
      } catch (cause) {
        notify(cause instanceof Error ? cause.message : "Mint failed", {
          type: "error",
        });
      } finally {
        setMinting(false);
      }
    },
    [notify],
  );

  const handleCopy = useCallback(
    async (code: TeponeAdminCode) => {
      try {
        await navigator.clipboard.writeText(code.portal_url);
        notify(`${code.code} link copied`, { type: "info" });
      } catch {
        notify("Could not copy the link", { type: "error" });
      }
    },
    [notify],
  );

  const handleQr = useCallback(
    async (code: TeponeAdminCode) => {
      try {
        await downloadQr(code);
      } catch {
        notify("QR generation failed", { type: "error" });
      }
    },
    [notify],
  );

  const summary = useMemo(() => {
    // Dev links are reusable internal tools — keep them out of the guest funnel.
    const guest = codes.filter((code) => !code.dev);
    const used = guest.filter((code) => USED_STATUSES.has(code.status)).length;
    const claimed = guest.filter(
      (code) => code.status === "claimed" || code.status === "delivered",
    ).length;
    return {
      total: guest.length,
      used,
      claimed,
      dev: codes.length - guest.length,
    };
  }, [codes]);

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Te Pone party links</CardTitle>
            <CardDescription>
              One-time QR codes for the guest portal. {summary.total} links —{" "}
              {summary.used} used ({summary.claimed} claimed by a guest)
              {summary.dev > 0 ? ` · ${summary.dev} dev` : ""}.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void load()}
              disabled={loading}
            >
              <RefreshCw className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleMint(true)}
              disabled={minting}
              title="Internal link: never expires and resets after every delivery, so the same link runs the full flow repeatedly"
            >
              <FlaskConical />
              {minting ? "Minting…" : "Generate dev link"}
            </Button>
            <Button onClick={() => void handleMint(false)} disabled={minting}>
              <Plus />
              {minting ? "Minting…" : "Generate link"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load Te Pone links</AlertTitle>
              <AlertDescription>
                {error}. The bot must be deployed with TEPONE_ENABLED and the
                CRM needs TEPONE_BOT_BASE_URL + TEPONE_PORTAL_API_KEY on its
                Vercel project (see src/server/teponeProxy.ts).
              </AlertDescription>
            </Alert>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead className="text-right">Media</TableHead>
                  <TableHead className="text-right">Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-muted-foreground"
                    >
                      No links yet — generate the first one.
                    </TableCell>
                  </TableRow>
                ) : (
                  codes.map((code) => {
                    const meta = STATUS_META[code.status] ?? {
                      label: code.status,
                      variant: "outline" as const,
                    };
                    return (
                      <TableRow key={code.token}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">
                              {code.code}
                            </span>
                            {code.dev ? (
                              <Badge
                                variant="outline"
                                className="border-amber-500 text-amber-600"
                                title="Internal dev link — never expires, resets after every delivery"
                              >
                                DEV · reusable
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {code.event_label || code.batch || ""}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={meta.variant}
                            className={meta.className}
                          >
                            {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(code.created_at)}
                        </TableCell>
                        <TableCell>
                          {code.wa_id ? (
                            <>
                              <div className="font-mono text-sm">
                                {code.wa_id}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatDate(code.claimed_at)}
                              </div>
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {code.media_count || "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Copy portal link"
                              onClick={() => void handleCopy(code)}
                            >
                              <Copy />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Download QR code"
                              onClick={() => void handleQr(code)}
                            >
                              <QrCode />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Open portal link"
                              asChild
                            >
                              <a
                                href={code.portal_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <ExternalLink />
                              </a>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TePoneCodesPage;
